import { MouseEventHandler, useCallback, useState } from 'react';
import clsx from 'clsx';
import {
  Avatar,
  useChannelActionContext,
  useChannelStateContext,
  useChatContext,
} from 'stream-chat-react';

import { PinIcon } from '@/assets';
import { Iconify } from '@/components';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';

import { ChannelInfoIcon } from './ChannelInfoIcon';
import {
  JobContextHeader,
  useJobContextHeaderData,
} from './JobContextHeader';
import type { JobContextHeaderData } from './JobContextHeader';
import { useWorkspaceController } from '@/contexts/WorkspaceController';

import type { StreamChatType } from '../../types';

type TeamChannelHeaderProps = {
  jobContext?: JobContextHeaderData;
};

const getRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

export const TeamChannelHeader = ({ jobContext }: TeamChannelHeaderProps) => {
  const { displayWorkspace } = useWorkspaceController();
  const { client, setActiveChannel } = useChatContext<StreamChatType>();
  const { channel, watcher_count } = useChannelStateContext<StreamChatType>();
  const { closeThread } = useChannelActionContext<StreamChatType>();
  const { togglePinnedMessageListOpen } = useWorkspaceController();
  const { errorMsg, successMsg } = useSnackbarMsg();
  const [isArchiving, setIsArchiving] = useState(false);

  const teamHeader = `# ${
    channel?.data?.name || channel?.data?.id || 'random'
  }`;
  const isJobSupportChannel = channel.type === 'jobSupport';
  const canArchiveChannel = channel.type !== 'team';
  const fallbackJobContext = useJobContextHeaderData(
    channel,
    isJobSupportChannel && !jobContext
  );
  const resolvedJobContext = jobContext ?? fallbackJobContext;

  const openChannelEditPanel = useCallback(() => {
    displayWorkspace('Admin-Channel-Edit');
  }, [displayWorkspace]);

  const onPinIconClick: MouseEventHandler = useCallback(
    (event) => {
      closeThread?.(event);
      togglePinnedMessageListOpen();
    },
    [closeThread, togglePinnedMessageListOpen]
  );

  const onArchiveClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (event) => {
      closeThread?.(event);

      if (!client.userID || isArchiving) return;

      setIsArchiving(true);
      try {
        if (isJobSupportChannel) {
          const channelData = getRecord(channel.data);
          const channelMetadata = getRecord(
            channelData.channelMetaData ??
              channelData.channelMetadata ??
              channelData.channel_meta_data
          );

          await channel.update({
            channelMetaData: {
              ...channelMetadata,
              archivedAt: new Date().toISOString(),
              archivedBy: client.userID,
              isArchived: true,
            },
          });
        } else {
          await channel.hide(client.userID, false);
        }

        successMsg('Chat archived');
        setIsArchiving(false);
        await setActiveChannel?.();
      } catch (error) {
        setIsArchiving(false);
        errorMsg(error, 'Unable to archive chat');
      }
    },
    [
      channel,
      client.userID,
      closeThread,
      errorMsg,
      isArchiving,
      isJobSupportChannel,
      setActiveChannel,
      successMsg,
    ]
  );

  const getMessagingHeader = () => {
    const members = Object.values(channel.state.members).filter(
      ({ user }) => user?.id !== client.userID
    );
    const additionalMembers = members.length - 3;

    if (!members.length) {
      return (
        <div className="workspace-header__block">
          <Avatar image={null} size={32} />
          <p className="team-channel-header__name user">Johnny Blaze</p>
        </div>
      );
    }

    return (
      <div className="workspace-header__block">
        {members.map(({ user }, i) => {
          if (i > 2) return null;
          return (
            <div key={i} className="workspace-header__block-item">
              <Avatar
                image={user?.image}
                name={user?.name || user?.id}
                size={32}
              />
              <p className="team-channel-header__name user">
                {user?.name || user?.id || 'Johnny Blaze'}
              </p>
            </div>
          );
        })}
        {additionalMembers > 0 && (
          <p className="team-channel-header__name user">{`and ${additionalMembers} more`}</p>
        )}
      </div>
    );
  };

  const getWatcherText = (watchers?: number) => {
    if (!watchers) return 'No users online';
    if (watchers === 1) return '1 user online';
    return `${watchers} users online`;
  };

  const renderTitleBlock = () =>
    channel.type === 'messaging' ? (
      getMessagingHeader()
    ) : (
      <div className="workspace-header__block">
        <div className="team-channel-header__name workspace-header__title">
          {teamHeader}
        </div>
        <button onClick={openChannelEditPanel}>
          <ChannelInfoIcon />
        </button>
      </div>
    );

  const renderActionBlock = () => (
    <div className="workspace-header__block">
      <div className="workspace-header__subtitle">
        {getWatcherText(watcher_count)}
      </div>
      <button className="workspace-header__subtitle" onClick={onPinIconClick}>
        <PinIcon />
        Pins
      </button>
      {canArchiveChannel && (
        <button
          aria-label="Archive chat"
          className="workspace-header__subtitle"
          disabled={isArchiving}
          onClick={onArchiveClick}
          title="Archive chat"
          type="button"
        >
          <Iconify icon="eva:archive-outline" width={16} />
          Archive
        </button>
      )}
    </div>
  );

  return (
    <div
      className={clsx('team-channel-header__container', {
        'team-channel-header__container--job': isJobSupportChannel,
      })}
    >
      {isJobSupportChannel ? (
        <>
          <div className="team-channel-header__top-row">
            <div className="team-channel-header__title-actions">
              {renderTitleBlock()}
            </div>
            {renderActionBlock()}
          </div>
          <JobContextHeader context={resolvedJobContext} />
        </>
      ) : (
        <>
          {renderTitleBlock()}
          {renderActionBlock()}
        </>
      )}
    </div>
  );
};
